import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "@/App.css";
import { FileUpload } from "@/components/FileUpload";
import { ModelViewerComponent } from "@/components/ModelViewerComponent";
import { MaterialSelector } from "@/components/MaterialSelector";
import { PrinterSelector } from "@/components/PrinterSelector";
import { EstimatorPanel } from "@/components/EstimatorPanel";
import { OptionalComponentsPanel } from "@/components/OptionalComponentsPanel";
import { CostBreakdown } from "@/components/CostBreakdown";
import { GlobalSettings } from "@/components/GlobalSettings";
import { LabourPanel } from "@/components/LabourPanel";
import { HelpModal } from "@/components/HelpModal";
import { api } from "@/utils/apiClient";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Calculator, Settings, DollarSign, Layers, Zap, TrendingUp, AlertCircle } from "lucide-react";
import { Toaster, toast } from "sonner";

const DEFAULT_LABOUR_TASKS = [
  { name: 'Review 3MF/STL', minutes: 10 },
  { name: 'Start Print', minutes: 1 },
  { name: 'Change Filament', minutes: 1 },
  { name: 'Clean Nozzle', minutes: 0.5 },
  { name: 'Spray Build Plate', minutes: 0.5 },
  { name: 'Remove Print', minutes: 0.5 },
];

function App() {
  // File and mesh data
  const [file, setFile] = useState(null);
  const [meshMetrics, setMeshMetrics] = useState(null);

  // Materials and printers
  const [materials, setMaterials] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [selectedPrinter, setSelectedPrinter] = useState(null);

  // Estimates
  const [weightEstimate, setWeightEstimate] = useState(null);
  const [timeEstimate, setTimeEstimate] = useState(null);
  const [manualWeight, setManualWeight] = useState(null);
  const [manualTime, setManualTime] = useState(null);
  const [useManualValues, setUseManualValues] = useState(false);
  const [customMaterialCost, setCustomMaterialCost] = useState(null);
  const [customPrinter, setCustomPrinter] = useState(null);
  const [slicing, setSlicing] = useState(false);
  const [slicingProgress, setSlicingProgress] = useState(0);
  const [estimatorSettings, setEstimatorSettings] = useState({
    layerHeight: 0.2,
    infillPercent: 20,
    shellThickness: 1.2,
    wasteFactor: 5
  });

  // COGS
  const [cogs, setCogs] = useState(null);
  const [cogsLoading, setCogsLoading] = useState(false);

  // Components
  const [optionalComponents, setOptionalComponents] = useState([]);

  // Labour
  const [labourTasks, setLabourTasks] = useState(DEFAULT_LABOUR_TASKS);

  // Settings
  const [globalSettings, setGlobalSettings] = useState({
    kwh_rate: 0.37,
    rent_year: 12000,
    labour_rate: 30,
    quantity_per_plate: 1
  });

  // Load materials and printers
  useEffect(() => {
    const loadData = async () => {
      try {
        const [materialsData, printersData] = await Promise.all([
          api.getMaterials(),
          api.getPrinters()
        ]);
        setMaterials(materialsData.materials);
        setPrinters(printersData.printers);
        
        if (materialsData.materials.length > 0) {
          setSelectedMaterial(materialsData.materials[0].id);
        }
        if (printersData.printers.length > 0) {
          // Default to Bambu Lab X1 Carbon if available, otherwise first printer
          const bambuPrinter = printersData.printers.find(p => p.id === 'bambu_x1c');
          setSelectedPrinter(bambuPrinter ? bambuPrinter.id : printersData.printers[0].id);
        }
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Failed to load materials and printers');
      }
    };
    loadData();
  }, []);

  // Parse filename when file is selected
  useEffect(() => {
    if (!file) return;

    const parseFilename = async () => {
      try {
        const result = await api.parseFilename(file.name);
        if (result.material && materials.find(m => m.id === result.material)) {
          setSelectedMaterial(result.material);
          toast.success(`Detected material: ${result.material.toUpperCase()}`);
        }
      } catch (error) {
        console.error('Error parsing filename:', error);
      }
    };
    parseFilename();
  }, [file, materials]);

  // Calculate COGS when estimates change
  useEffect(() => {
    if (!weightEstimate || !timeEstimate || !selectedPrinter || !selectedMaterial) return;

    const calculateCOGS = async () => {
      setCogsLoading(true);
      try {
        const finalWeight = useManualValues && manualWeight ? manualWeight : weightEstimate.grams;
        const finalTime = useManualValues && manualTime ? manualTime : timeEstimate.hours;

        const basePrinter = selectedPrinter === 'custom' && customPrinter 
          ? customPrinter 
          : printers.find(p => p.id === selectedPrinter);
        const material = materials.find(m => m.id === selectedMaterial);
        const finalMaterialCost = customMaterialCost !== null ? customMaterialCost : material.cost_per_kg;

        // Normalize printer properties for custom vs standard printers
        const printer = selectedPrinter === 'custom' ? {
          power_consumption_kw: customPrinter.power_consumption_w / 1000,
          cost: customPrinter.machine_cost,
          lifetime_hours: customPrinter.lifetime_hours,
          footprint_m2: customPrinter.footprint_sqm
        } : {
          power_consumption_kw: basePrinter.power_consumption_kw,
          cost: basePrinter.cost,
          lifetime_hours: basePrinter.lifetime_hours,
          footprint_m2: basePrinter.footprint_m2
        };

        const cogsData = await api.calculateCOGS({
          grams: finalWeight,
          hours: finalTime,
          kwh_rate: globalSettings.kwh_rate,
          printer_power_kw: printer.power_consumption_kw,
          printer_cost: printer.cost,
          printer_life_hours: printer.lifetime_hours,
          rent_year: globalSettings.rent_year,
          footprint_m2: printer.footprint_m2,
          labour_tasks: labourTasks,
          labour_rate: globalSettings.labour_rate,
          material_cost_per_kg: finalMaterialCost,
          optional_components: optionalComponents,
          quantity_per_plate: globalSettings.quantity_per_plate
        });

        setCogs(cogsData);
      } catch (error) {
        console.error('Error calculating COGS:', error);
        toast.error('Failed to calculate COGS');
      } finally {
        setCogsLoading(false);
      }
    };

    calculateCOGS();
  }, [
    weightEstimate,
    timeEstimate,
    selectedPrinter,
    selectedMaterial,
    printers,
    materials,
    globalSettings,
    labourTasks,
    optionalComponents,
    useManualValues,
    manualWeight,
    manualTime,
    customMaterialCost,
    customPrinter
  ]);

  const handleFileSelect = (selectedFile) => {
    setFile(selectedFile);
    setMeshMetrics(null);
    setWeightEstimate(null);
    setTimeEstimate(null);
    setCogs(null);
    toast.success('File uploaded successfully');
  };

  const handleFileClear = () => {
    setFile(null);
    setMeshMetrics(null);
    setWeightEstimate(null);
    setTimeEstimate(null);
    setCogs(null);
  };

  const handleMeshMetrics = useCallback((metrics) => {
    setMeshMetrics(metrics);
    if (metrics.warnings && metrics.warnings.length > 0) {
      metrics.warnings.forEach(warning => {
        toast.warning(warning);
      });
    }
  }, []);

  const handlePrusaSlice = async () => {
    if (!file) return;
    
    setSlicing(true);
    setSlicingProgress(0);
    toast.info('Starting PrusaSlicer... Using your print settings');
    
    try {
      const formData = new FormData();
      // Create a fresh File object to avoid stream consumption issues on re-slice
      formData.append('file', new File([file], file.name, { type: file.type }));
      formData.append('layer_height', estimatorSettings.layerHeight.toString());
      formData.append('infill_density', estimatorSettings.infillPercent.toString());
      formData.append('support_material', 'false');
      formData.append('print_speed', '50');
      
      console.log('Slicing with settings:', {
        layer_height: estimatorSettings.layerHeight,
        infill: estimatorSettings.infillPercent
      });
      
      // Simulate progress
      const progressInterval = setInterval(() => {
        setSlicingProgress(prev => {
          if (prev >= 90) return prev;
          return prev + 10;
        });
      }, 5000);
      
      // Use axios directly with full URL
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/slicer/slice`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      clearInterval(progressInterval);
      setSlicingProgress(100);
      
      const data = response.data;
      console.log('Slicing result:', data);
      
      // Set the accurate values from PrusaSlicer
      setManualWeight(data.filament_weight_g);
      setManualTime(data.time_hours);
      setUseManualValues(true);
      
      // Keep estimated values so the section remains visible, but manual values take precedence
      if (!weightEstimate) {
        setWeightEstimate({ grams: data.filament_weight_g });
      }
      if (!timeEstimate) {
        setTimeEstimate({ hours: data.time_hours });
      }
      
      toast.success(`✅ Slicing complete! Weight: ${data.filament_weight_g}g, Time: ${data.time_hours}h`);
    } catch (error) {
      console.error('PrusaSlicer error:', error);
      const errorMessage = error.response?.data?.error || error.message;
      toast.error(`Failed to slice: ${errorMessage}`);
    } finally {
      setSlicing(false);
      setSlicingProgress(0);
    }
  };

  const handleWeightEstimate = async (params) => {
    try {
      const estimate = await api.estimateWeight(params);
      setWeightEstimate(estimate);
    } catch (error) {
      console.error('Error estimating weight:', error);
    }
  };

  const handleTimeEstimate = async (params) => {
    try {
      const estimate = await api.estimateTime(params);
      setTimeEstimate(estimate);
    } catch (error) {
      console.error('Error estimating time:', error);
    }
  };

  const currentMaterial = materials.find(m => m.id === selectedMaterial);
  const currentPrinter = printers.find(p => p.id === selectedPrinter);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-x-hidden w-full">
      <Toaster position="top-right" richColors />
      
      {/* Premium Header */}
      <div className="sticky top-0 z-50 border-b border-gray-200 bg-white/80 backdrop-blur-xl shadow-sm">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-50 via-transparent to-purple-100" />
        <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-5 relative max-w-full overflow-hidden">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <img 
                src="/logo.png" 
                alt="3DPG Logo" 
                className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl shadow-lg"
              />
              <div>
                <h1 className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                  3D Print Profit Calculator
                </h1>
                <p className="text-xs text-gray-500 hidden sm:block">Professional cost estimation tool by 3D Printer Gear Pty Ltd</p>
              </div>
            </div>
            <HelpModal />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 py-8 max-w-full overflow-x-hidden">
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-4 sm:gap-6">
          {/* Left Panel - Controls (2/5 width) */}
          <div className="xl:col-span-2 space-y-4 sm:space-y-6">
            <FileUpload 
              onFileSelect={handleFileSelect} 
              selectedFile={file}
              onClear={handleFileClear}
            />

            <Tabs defaultValue="materials" className="w-full">
              <TabsList className="grid w-full grid-cols-3 bg-gray-100 border border-gray-200 p-1 shadow-sm">
                <TabsTrigger 
                  value="materials" 
                  className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-purple-600 data-[state=active]:text-white transition-all data-[state=active]:shadow-md"
                >
                  <Layers className="h-4 w-4 mr-2" />
                  Materials
                </TabsTrigger>
                <TabsTrigger 
                  value="settings" 
                  className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-purple-600 data-[state=active]:text-white transition-all data-[state=active]:shadow-md"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Settings
                </TabsTrigger>
                <TabsTrigger 
                  value="costs" 
                  className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-purple-600 data-[state=active]:text-white transition-all data-[state=active]:shadow-md"
                >
                  <DollarSign className="h-4 w-4 mr-2" />
                  Costs
                </TabsTrigger>
              </TabsList>

              <TabsContent value="materials" className="space-y-4 mt-4">
                {/* Material First */}
                {materials.length > 0 && (
                  <MaterialSelector
                    materials={materials}
                    selected={selectedMaterial}
                    onSelect={setSelectedMaterial}
                    onCostChange={setCustomMaterialCost}
                  />
                )}
                
                {/* Print Parameters Second */}
                {currentMaterial && (
                  <EstimatorPanel
                    metrics={meshMetrics}
                    material={currentMaterial}
                    onWeightEstimate={handleWeightEstimate}
                    onTimeEstimate={handleTimeEstimate}
                    onSettingsChange={setEstimatorSettings}
                  />
                )}
                
                {/* Printer Third */}
                {printers.length > 0 && (
                  <PrinterSelector
                    printers={printers}
                    selected={selectedPrinter}
                    onSelect={setSelectedPrinter}
                    onCustomPrinterChange={setCustomPrinter}
                  />
                )}
              </TabsContent>

              <TabsContent value="settings" className="space-y-4 mt-4">
                <GlobalSettings
                  settings={globalSettings}
                  onChange={setGlobalSettings}
                />
                <LabourPanel
                  tasks={labourTasks}
                  onChange={setLabourTasks}
                />
              </TabsContent>

              <TabsContent value="costs" className="space-y-4 mt-4">
                <OptionalComponentsPanel
                  components={optionalComponents}
                  onChange={setOptionalComponents}
                />
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Panel - Viewer and Results (3/5 width) */}
          <div className="xl:col-span-3 space-y-4 sm:space-y-6">
            {/* Pricing Suggestions - TOP PRIORITY */}
            {cogs && (
              <Card className="p-4 sm:p-6 bg-gradient-to-br from-purple-50 via-purple-100/30 to-purple-50 border-purple-200 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-purple-500/20">
                    <DollarSign className="h-5 w-5 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-base sm:text-lg text-gray-800">
                    Suggested Pricing
                  </h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                  <div className="p-4 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 transition-all shadow-sm">
                    <div className="text-xs text-gray-600 mb-1">2x Markup</div>
                    <div className="text-2xl font-bold text-purple-600">
                      ${(cogs.total_cogs_per_part * 2).toFixed(2)}
                    </div>
                    <div className="text-xs text-purple-500 mt-1">100% margin</div>
                  </div>
                  <div className="p-4 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 transition-all shadow-sm">
                    <div className="text-xs text-gray-600 mb-1">3x Markup</div>
                    <div className="text-2xl font-bold text-purple-600">
                      ${(cogs.total_cogs_per_part * 3).toFixed(2)}
                    </div>
                    <div className="text-xs text-purple-500 mt-1">200% margin</div>
                  </div>
                  <div className="p-4 rounded-xl bg-gradient-to-br from-purple-100 to-purple-50 border border-purple-300 hover:scale-105 transition-transform shadow-md">
                    <div className="text-xs text-purple-700 mb-1 font-medium">4x Markup ⭐</div>
                    <div className="text-2xl font-bold text-purple-700">
                      ${(cogs.total_cogs_per_part * 4).toFixed(2)}
                    </div>
                    <div className="text-xs text-purple-600 mt-1">300% margin</div>
                  </div>
                </div>
              </Card>
            )}
            
            {/* 3D Viewer */}
            <Card className="h-[500px] bg-white border-gray-200 overflow-hidden shadow-xl">
              <ModelViewerComponent
                file={file}
                onMetricsCalculated={handleMeshMetrics}
              />
            </Card>

            {/* Estimates Summary with Manual Override */}
            {(weightEstimate || timeEstimate) && (
              <div className="space-y-4">
                {/* PrusaSlicer Option */}
                {file && (
                  <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100/30 border-purple-200">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="text-sm font-semibold text-purple-800 mb-1">
                          Get Accurate Values with PrusaSlicer
                        </div>
                        <div className="text-xs text-purple-600">
                          Uses your print settings: {estimatorSettings.layerHeight}mm layers, {estimatorSettings.infillPercent}% infill
                        </div>
                      </div>
                      <Button
                        onClick={handlePrusaSlice}
                        disabled={slicing}
                        className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white disabled:opacity-50 shadow-md"
                      >
                        {slicing ? 'Slicing...' : 'Slice Now'}
                      </Button>
                    </div>
                    {slicing && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs text-purple-700">
                          <span>Processing with PrusaSlicer...</span>
                          <span>{slicingProgress}%</span>
                        </div>
                        <div className="w-full bg-purple-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${slicingProgress}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </Card>
                )}
                
                {/* Toggle for manual values - Only show if not sliced yet */}
                {!manualWeight && !manualTime && (
                  <div className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-yellow-600" />
                      <span className="text-sm font-medium text-yellow-800">
                        Want to enter values manually? Toggle here to input from your own slicer
                      </span>
                    </div>
                    <Switch
                      checked={useManualValues}
                      onCheckedChange={(checked) => {
                        setUseManualValues(checked);
                        if (checked) {
                          // Initialize with 0 to show input fields
                          setManualWeight(0);
                          setManualTime(0);
                        }
                      }}
                    />
                  </div>
                )}

                {/* Always show weight/time fields */}
                <div className="grid grid-cols-2 gap-4">
                  <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200 shadow-md">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-lg bg-blue-500/20">
                        <Zap className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="text-sm text-blue-700">Weight (g)</div>
                    </div>
                    {manualWeight !== null ? (
                      <Input
                        type="number"
                        value={manualWeight}
                        onChange={(e) => setManualWeight(parseFloat(e.target.value) || 0)}
                        placeholder="Enter weight"
                        className="text-2xl font-bold h-14"
                      />
                    ) : (
                      <div className="text-4xl font-bold text-gray-400 h-14 flex items-center justify-center">
                        -
                      </div>
                    )}
                  </Card>
                  
                  <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100/50 border-purple-200 shadow-md">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-lg bg-purple-500/20">
                        <TrendingUp className="h-5 w-5 text-purple-600" />
                      </div>
                      <div className="text-sm text-purple-700">Print Time (h)</div>
                    </div>
                    {manualTime !== null ? (
                      <Input
                        type="number"
                        step="0.01"
                        value={manualTime}
                        onChange={(e) => setManualTime(parseFloat(e.target.value) || 0)}
                        placeholder="Enter time"
                        className="text-2xl font-bold h-14"
                      />
                    ) : (
                      <div className="text-4xl font-bold text-gray-400 h-14 flex items-center justify-center">
                        -
                      </div>
                    )}
                  </Card>
                </div>
              </div>
            )}

            {/* COGS Breakdown */}
            <CostBreakdown 
              cogs={cogs} 
              loading={cogsLoading}
              quantityPerPlate={globalSettings.quantity_per_plate}
              modelMetrics={meshMetrics}
              materialName={currentMaterial?.name}
              printerName={currentPrinter?.name || customPrinter?.name}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
